import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from './AuthContext';
import './App.css';
import EnterImage from './Enter.svg';
import mobileImage from './Rectangle 309.png';
import ConnectWalletHeader from './tables/header/ConnectWalletHeader';
import {ENDPOINT_BASE_URL} from './configs/config';
import bs58 from 'bs58';

const ADMIN_MESSAGE = "Sign this message to authenticate as admin for PrismaX AI Admin Portal.";

const LoginPage = () => {
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [showPanel, setShowPanel] = useState(false);
    const [walletInfo, setWalletInfo] = useState(null);
    const [isSigning, setIsSigning] = useState(false);
    const auth = useAuth();
    const navigate = useNavigate();

    const handleWalletProviderReady = ({provider, publicKey}) => {
        setWalletInfo({provider, publicKey});
        setError('');
        setStatus('Wallet connected. Please sign to continue.');
    };

    const requestSignature = async (provider) => {
        const encodedMessage = new TextEncoder().encode(ADMIN_MESSAGE);
        let signature;

        if (provider?.signMessage) {
            const result = await provider.signMessage(encodedMessage);
            signature = result?.signature ?? result;
        } else if (provider?.request) {
            const response = await provider.request({
                method: 'signMessage',
                params: {
                    message: encodedMessage,
                    display: 'utf8'
                }
            });
            signature = response?.signature ?? response;
        } else {
            throw new Error('Wallet does not support message signing.');
        }

        if (signature instanceof Uint8Array) {
            return bs58.encode(signature);
        }
        if (typeof signature === 'string') {
            return signature;
        }
        if (signature && signature.signature instanceof Uint8Array) {
            return bs58.encode(signature.signature);
        }
        throw new Error('Unexpected signature format.');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!walletInfo?.provider || !walletInfo?.publicKey) {
            setError('Please connect your wallet first.');
            return;
        }
        setIsSigning(true);
        setError('');
        setStatus('Signing message...');
        try {
            const signature = await requestSignature(walletInfo.provider);
            const response = await fetch(`${ENDPOINT_BASE_URL}/admin-login`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    wallet_address: walletInfo.publicKey,
                    signature
                })
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                const error = new Error(data.msg || 'Admin login failed');
                error.code = data?.code;
                error.data = data;
                error.status = response.status;
                error.statusText = response.statusText;
                console.error('Admin login failed:', {
                    code: error.code,
                    message: error.message,
                    data: error.data,
                    status: error.status,
                    statusText: error.statusText
                });
                throw error;
            }
            const {access_token, refresh_token} = await response.json();
            auth.setAuthTokens({
                accessToken: access_token,
                refreshToken: refresh_token,
                expiresInMs: 60 * 60 * 1000
            });
            setStatus('Login successful. Redirecting...');
            navigate('/home/usdi-stats');
        } catch (err) {
            console.error('Admin login error:', {
                code: err?.code,
                message: err?.message,
                data: err?.data,
                status: err?.status,
                statusText: err?.statusText
            });
            setError(err.message || 'Login failed');
            setStatus('');
        } finally {
            setIsSigning(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-form">
                <div className="login-wallet-container">
                    <ConnectWalletHeader
                        isAdminHeader={false}
                        showPanel={showPanel}
                        setShowPanel={setShowPanel}
                        onWalletProviderReady={handleWalletProviderReady}
                        skipWalletDataFetch={true}
                    />
                </div>
                <img className="mobile-image" src={mobileImage} alt="Login"/>
                <div className="green-header"><h2>Admin Login</h2></div>
                <p>Connect your Solana wallet and click Enter to continue.</p>
                <form onSubmit={handleLogin}>
                    {error && <p className="error">{error}</p>}
                    {status && !error && <p>{status}</p>}
                    <button type="submit" disabled={!walletInfo || isSigning}>
                        <img className="enter" src={EnterImage} alt=""/>
                    </button>
                </form>
            </div>
            <div className="login-image">
                <img src="/images/login.png" alt="Login"/>
            </div>
        </div>
    );
};

export default LoginPage;
